import { useMemo } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Tooltip from '@mui/material/Tooltip';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { HrefLink } from '@bloomays-lib/ui.shared';
import { GridColDef, GridRenderCellParams, GridRowClassNameParams, gridClasses } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import RawDataGrid from './DataGrid';
import { getJobTypeGroupLabel } from '@bloomays-lib/utils.shared';
import { JobType, PRIME_LINE_ERROR_CODES } from '@bloomays-lib/types.shared';
import { TamPrimeLine } from '../../types/monthlyPrime';
import {
  formatPrimeEuro,
  formatPrimeRate,
  isPlainCraStatusDisplay,
  isPrimeLineError,
  shouldShowMissionActivitiesLinkForCraStatus,
} from '../../utils/monthlyPrime.utils';

export type MonthlyPrimeTableProps = {
  rows: TamPrimeLine[];
  loading: boolean;
};

const MonthlyPrimeTable = ({ rows, loading }: MonthlyPrimeTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['primes']);

  const columns: GridColDef<TamPrimeLine>[] = useMemo(
    () => [
      {
        field: 'clientSocietyName',
        headerName: t('columnSociety'),
        flex: 1,
        minWidth: 120,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
      },
      {
        field: 'bloomerFullName',
        headerName: t('columnBloomer'),
        flex: 1,
        minWidth: 140,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
      },
      {
        field: 'jobType',
        headerName: t('columnContractType'),
        width: 120,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: JobType) => getJobTypeGroupLabel(value),
      },
      {
        field: 'craStatus',
        headerName: t('columnCraStatus'),
        width: 180,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueGetter: (_value, row) => ('craStatus' in row ? row.craStatus : ''),
        renderCell: (params: GridRenderCellParams<TamPrimeLine>) => {
          const craStatus = 'craStatus' in params.row ? params.row.craStatus : '';
          if (!craStatus) {
            return null;
          }
          if (isPlainCraStatusDisplay(craStatus)) {
            return craStatus;
          }
          const missionActivitiesHref =
            shouldShowMissionActivitiesLinkForCraStatus(craStatus) && params.row.airtableId.trim() !== ''
              ? `/missions/${params.row.airtableId}/activities`
              : undefined;
          return (
            <AlertCell>
              <span aria-hidden="true">🚨</span> {craStatus}
              {missionActivitiesHref ? (
                <HrefLink href={missionActivitiesHref} target="_blank" title={t('openMissionActivities')}>
                  <OpenInNewIcon fontSize="small" />
                </HrefLink>
              ) : null}
            </AlertCell>
          );
        },
      },
      {
        field: 'monthlyMargin',
        headerName: t('columnMargin'),
        type: 'number',
        width: 110,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeEuro(value),
        renderCell: (params: GridRenderCellParams<TamPrimeLine>) => {
          const marginLabel = formatPrimeEuro(params.row.monthlyMargin);
          if (params.row.errorCode !== PRIME_LINE_ERROR_CODES.MISSING_PRICING) {
            return marginLabel;
          }
          return (
            <AlertCell>
              {marginLabel}
              <Tooltip title={t('missingPricingTooltip')}>
                <span role="img" aria-label={t('missingPricingTooltip')}>
                  🚨
                </span>
              </Tooltip>
            </AlertCell>
          );
        },
      },
      {
        field: 'monthlyPrime',
        headerName: t('columnCommission'),
        type: 'number',
        width: 120,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeEuro(value),
      },
      {
        field: 'splitRate',
        headerName: t('columnTamShare'),
        type: 'number',
        width: 110,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeRate(value),
      },
      {
        field: 'commissionRate',
        headerName: t('columnCommissionRate'),
        type: 'number',
        width: 130,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeRate(value),
      },
    ],
    [t],
  );

  return (
    <TableSection>
      {!loading && rows.length === 0 ? <EmptyMessage variant="body2">{t('emptyRows')}</EmptyMessage> : null}
      <RawDataGrid
        uniqueDatagridId="monthly-prime-lines"
        fetchLoading={loading}
        getRowClassName={(params: GridRowClassNameParams<TamPrimeLine>) => {
          if (isPrimeLineError(params.row)) {
            return 'error';
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
        }}
        hideFooterPagination={rows.length <= 25}
        sortingMode="client"
        filterMode="client"
        rows={rows}
        getRowId={(row: TamPrimeLine) => `${row.jobFunnelProjectId}-${row.airtableId}`}
        columns={columns}
        pageSize={25}
        pagination={rows.length > 25}
        sx={{
          width: '100%',
          [`& .${gridClasses.row}.even`]: {
            backgroundColor: theme.palette.neutral[10],
          },
          [`& .${gridClasses.row}.error`]: {
            backgroundColor: theme.palette.red[20],
          },
          [`& .${gridClasses.row}.error .${gridClasses.cell}`]: {
            backgroundColor: theme.palette.red[20],
          },
        }}
      />
    </TableSection>
  );
};

export default MonthlyPrimeTable;

const TableSection = styled('div')(() => ({
  marginTop: '24px',
  width: '100%',
}));

const AlertCell = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
}));
